"use client";

import { useState } from "react";
import Link from "next/link";
import {
  signInPassword,
  sendOtp,
  signInOtp,
  forgetPassword,
} from "@/lib/auth-client";
import { Logo } from "@/components/Logo";
import { getAuth } from "@/content/auth";
import { PasswordField } from "@/components/auth/PasswordField";
import { localePath, DEFAULT_LOCALE, type Locale } from "@/lib/i18n";

type Mode = "password" | "otp-email" | "otp-code";

/**
 * Sign in with email + password (primary), or fall back to an email code. There is no
 * "remember me": the server caps every session at one hour.
 * Password reset is a one-click t.forgot that emails a reset link (Resend).
 */
export function LoginBody({ locale = DEFAULT_LOCALE }: { locale?: Locale }) {
  const a = getAuth(locale);
  const t = a.login;
  const L = (p: string) => localePath(locale, p);
  const [mode, setMode] = useState<Mode>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // FULL navigation (not router.replace): sign-in goes through better-auth's raw `$fetch`, which
  // sets the session cookie but does NOT update the reactive `useSession` store — so a soft
  // client nav lands on the dashboard while it still reads "no session" and bounces back to /login
  // (the "click Sign in twice" bug). A real page load re-inits the app so `useSession` fetches the
  // now-live session. `replace` keeps /login out of history, matching the old intent.
  /**
   * Return the user to where they were bounced from, not always the dashboard.
   *
   * `?next=` is set by lib/api.ts when a request 401s mid-session. Honouring it is what makes
   * that parameter truthful — it was added first and ignored here, which is worse than not
   * having it.
   *
   * OPEN-REDIRECT GUARD, and it is not optional: `next` comes from the URL, so a link like
   * `/login?next=https://evil.example/harvest` would send a freshly-authenticated user off-site
   * to a convincing clone. Only same-origin PATHS are accepted — must start with a single "/",
   * and "//host" is rejected because browsers treat it as protocol-relative, i.e. another origin.
   */
  const safeNext = (): string => {
    if (typeof window === "undefined") return "/dashboard";
    const raw = new URLSearchParams(window.location.search).get("next");
    if (!raw || !raw.startsWith("/") || raw.startsWith("//"))
      return "/dashboard";
    return raw;
  };
  const done = () => window.location.replace(safeNext());

  const passwordLogin = async () => {
    setBusy(true);
    setErr(null);
    // `true`, not a user choice: sessions are capped at ONE HOUR server-side
    // (services/gateway/src/auth.ts), so "Remember me" could not be honoured and its
    // checkbox was removed. Passing true keeps the cookie across a browser restart
    // within that hour — the cap the owner asked for is on TIME, not on closing the tab.
    const { error } = await signInPassword(email, password, true);
    setBusy(false);
    if (error) return setErr(error.message ?? "invalid email or password");
    done();
  };

  const sendCode = async () => {
    setBusy(true);
    setErr(null);
    const { error } = await sendOtp(email);
    setBusy(false);
    if (error) return setErr(error.message ?? "could not send code");
    setMode("otp-code");
  };

  const codeLogin = async () => {
    setBusy(true);
    setErr(null);
    const { error } = await signInOtp(email, otp);
    setBusy(false);
    if (error) return setErr(error.message ?? "invalid code");
    done();
  };

  const forgot = async () => {
    if (!email) return setErr(t.errNoEmail);
    setBusy(true);
    setErr(null);
    setNote(null);
    await forgetPassword(email);
    setBusy(false);
    setNote(t.resetSent);
  };

  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      <div className="panel bracketed w-full max-w-sm space-y-4">
        <div>
          <Logo />
          <p className="mt-3 text-sm text-muted">{t.lead}</p>
        </div>

        <div>
          <label className="label">{t.email}</label>
          <input
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </div>

        {mode === "password" && (
          <>
            <div>
              <label className="label">{t.password}</label>
              <PasswordField
                value={password}
                onChange={setPassword}
                autoComplete="current-password"
                showLabel={a.showPassword}
                hideLabel={a.hidePassword}
                onKeyDown={(e) =>
                  e.key === "Enter" && email && password && passwordLogin()
                }
              />
            </div>
            <button
              className="btn w-full"
              disabled={busy || !email || !password}
              onClick={passwordLogin}
            >
              {busy ? t.submitting : t.submit}
            </button>
            <div className="flex justify-between text-xs">
              <button
                className="text-accent hover:underline"
                onClick={() => {
                  setMode("otp-email");
                  setErr(null);
                }}
              >
                {t.useCode}
              </button>
              <button className="text-muted hover:underline" onClick={forgot}>
                {t.forgot}
              </button>
            </div>
          </>
        )}

        {mode === "otp-email" && (
          <>
            <button
              className="btn w-full"
              disabled={busy || !email}
              onClick={sendCode}
            >
              {busy ? t.codeSending : t.codeCta}
            </button>
            <button
              className="text-xs text-accent hover:underline"
              onClick={() => {
                setMode("password");
                setErr(null);
              }}
            >
              {t.backToPassword}
            </button>
          </>
        )}

        {mode === "otp-code" && (
          <>
            <div>
              <label className="label">{t.codeLabel} · {email}</label>
              <input
                className="input tracking-[0.4em]"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="000000"
                onKeyDown={(e) =>
                  e.key === "Enter" && otp.length === 6 && codeLogin()
                }
              />
            </div>
            <button
              className="btn w-full"
              disabled={busy || otp.length !== 6}
              onClick={codeLogin}
            >
              {busy ? t.verifying : t.verify}
            </button>
          </>
        )}

        {note && <p className="text-sm text-good">{note}</p>}
        {err && <p className="text-sm text-bad">{err}</p>}

        <p className="border-t border-border/60 pt-3 text-center text-sm text-muted">
          {t.noAccount}{" "}
          <Link href={L("/signup")} className="text-accent hover:underline">
            {t.createOne}
          </Link>
        </p>
      </div>
    </main>
  );
}
